// Comms polish hotfix (1.12.233-beta): a new deploy reaches tabs and the installed app that are already open.
//
// Nov 4: after #776 went live, open windows kept running the OLD bundle for 20+ minutes (still sending the removed query).
// The staff app's only service worker (public/push-worker.js) never caches the app's files, so nothing was serving a stale
// copy: the open window simply never loaded the new one. This watches for a newer version in two ways —
//   1. the existing service worker registration is asked to check for a new worker file (on load, every few minutes and when
//      the window comes back into view), and a worker left WAITING counts as a new version;
//   2. the page's own HTML is re-read (no-store) and its main script compared with the one this window is running.
// When either finds one, a small "New version available — Reload" pill shows. Reload first asks every open composer to save
// its unsent text (it comes back after the reload), warns about any it could not save, then activates the waiting worker
// (if any) and reloads. Plain JS, no React, so it is easy to test.

export const APP_UPDATE_CHECK_MS = 5 * 60 * 1000;
export const SAVE_DRAFTS_EVENT = 'ia:save-drafts-before-reload';
export const SKIP_WAITING_MESSAGE = { type: 'ia-skip-waiting' };

/** The main script this window is running, e.g. "/static/js/main.1a2b3c.js" (null in the dev server, which has no hash). */
export function currentMainScript(doc = typeof document !== 'undefined' ? document : null) {
    if (!doc) return null;
    const scripts = Array.from(doc.querySelectorAll('script[src]')).map((node) => node.getAttribute('src') || '');
    return mainScriptFrom(scripts);
}

function mainScriptFrom(sources) {
    const hit = sources.find((src) => /\/static\/js\/main\.[0-9a-f]+\.js(\?|$)/i.test(src));
    if (!hit) return null;
    try { return new URL(hit, 'https://x.invalid').pathname; } catch { return hit; }
}

/** The main script named by a freshly fetched HTML document (null when it names none). */
export function mainScriptInHtml(html) {
    const sources = [];
    const pattern = /<script[^>]*\ssrc=["']([^"']+)["']/gi;
    let match = pattern.exec(String(html || ''));
    while (match) { sources.push(match[1]); match = pattern.exec(String(html || '')); }
    return mainScriptFrom(sources);
}

/** True when the server's HTML now points at a different main script than the one this window loaded. */
export async function serverHasNewerBundle({ fetchImpl = typeof fetch === 'function' ? fetch : null, doc, origin } = {}) {
    const running = currentMainScript(doc);
    if (!running || !fetchImpl) return false;
    try {
        const base = origin || (typeof window !== 'undefined' ? window.location.origin : '');
        const response = await fetchImpl(`${base}/?ia-version-check=${Date.now()}`, { cache: 'no-store', credentials: 'same-origin' });
        if (!response || !response.ok) return false;
        const served = mainScriptInHtml(await response.text());
        return Boolean(served && served !== running);
    } catch {
        return false; // offline: try again next time
    }
}

/**
 * Asks every open composer to save its unsent text for after the reload. Returns how many were saved and how many have text
 * that could not be saved (the caller warns before reloading over those).
 */
export function saveDraftsBeforeReload(win = typeof window !== 'undefined' ? window : null) {
    const detail = { saved: 0, unsaved: 0 };
    if (!win) return detail;
    try { win.dispatchEvent(new win.CustomEvent(SAVE_DRAFTS_EVENT, { detail })); } catch { /* no listeners */ }
    return detail;
}

/**
 * A composer listens while it is on screen. `save(detail)` returns 'saved', 'empty' or 'unsaved' (detail.saved says how many
 * composers already saved during this reload — there is one restore slot per tab). Returns the unsubscribe function.
 */
export function onSaveDraftsBeforeReload(save, win = typeof window !== 'undefined' ? window : null) {
    if (!win || typeof save !== 'function') return () => {};
    const listener = (event) => {
        const detail = event?.detail;
        if (!detail) return;
        let result = 'unsaved';
        try { result = save(detail); } catch { result = 'unsaved'; }
        if (result === 'saved') detail.saved += 1;
        else if (result === 'unsaved') detail.unsaved += 1;
    };
    win.addEventListener(SAVE_DRAFTS_EVENT, listener);
    return () => win.removeEventListener(SAVE_DRAFTS_EVENT, listener);
}

/**
 * Activates a waiting worker (if any) and reloads. Drafts are saved first; when some text could not be saved the person is
 * asked, and nothing happens if they say no. Returns false when the reload was cancelled.
 */
export function applyAppUpdate({
    waiting = null,
    win = typeof window !== 'undefined' ? window : null,
    confirmImpl,
    reloadImpl,
} = {}) {
    if (!win) return false;
    const drafts = saveDraftsBeforeReload(win);
    const ask = confirmImpl || ((text) => win.confirm(text));
    if (drafts.unsaved > 0 && !ask('You have an unsent message that could not be saved. Reload anyway and lose it?')) return false;
    const reload = reloadImpl || (() => win.location.reload());
    let reloaded = false;
    const once = () => { if (reloaded) return; reloaded = true; reload(); };
    if (waiting && typeof waiting.postMessage === 'function') {
        const sw = win.navigator?.serviceWorker;
        sw?.addEventListener?.('controllerchange', once);
        try { waiting.postMessage(SKIP_WAITING_MESSAGE); } catch { /* the worker is gone; just reload */ }
        // A worker that never takes over must not leave the person stuck: reload anyway shortly after.
        win.setTimeout(once, 1500);
        return true;
    }
    once();
    return true;
}

/**
 * Starts watching for a newer version. `onUpdate({ waiting })` is called once, the first time one is found (waiting is the
 * waiting service worker, or null for a newer bundle). Returns a stop function.
 */
export function watchForAppUpdate(onUpdate, {
    win = typeof window !== 'undefined' ? window : null,
    intervalMs = APP_UPDATE_CHECK_MS,
    fetchImpl,
} = {}) {
    if (!win || typeof onUpdate !== 'function') return () => {};
    let stopped = false;
    let announced = false;
    const announce = (waiting = null) => {
        if (stopped || announced) return;
        announced = true;
        onUpdate({ waiting });
    };
    const watchRegistration = (registration) => {
        if (!registration) return;
        if (registration.waiting && win.navigator.serviceWorker.controller) announce(registration.waiting);
        registration.addEventListener?.('updatefound', () => {
            const installing = registration.installing;
            installing?.addEventListener?.('statechange', () => {
                if (installing.state === 'installed' && win.navigator.serviceWorker.controller && registration.waiting) announce(registration.waiting);
            });
        });
    };
    const check = async () => {
        if (stopped || announced) return;
        try {
            const sw = win.navigator?.serviceWorker;
            const registration = sw?.getRegistration ? await sw.getRegistration() : null;
            if (registration) {
                if (typeof registration.update === 'function') await registration.update().catch(() => {});
                if (registration.waiting && sw.controller) { announce(registration.waiting); return; }
            }
        } catch { /* no worker on this device */ }
        if (await serverHasNewerBundle({ fetchImpl, doc: win.document, origin: win.location?.origin })) announce(null);
    };
    (async () => {
        try {
            const sw = win.navigator?.serviceWorker;
            if (sw?.getRegistration) watchRegistration(await sw.getRegistration());
        } catch { /* no worker */ }
        check();
    })();
    const timer = win.setInterval(check, intervalMs);
    const onVisible = () => { if (win.document?.visibilityState === 'visible') check(); };
    win.document?.addEventListener?.('visibilitychange', onVisible);
    return () => {
        stopped = true;
        win.clearInterval(timer);
        win.document?.removeEventListener?.('visibilitychange', onVisible);
    };
}
